import { useTranslation } from 'react-i18next';

import {
	Box,
	Button,
	Divider,
	Flex,
	Grid,
	HeartSolid,
	LocationMarkerSolid,
	PopoverBody,
	PopoverContent,
	Portal,
	rem,
	Stack,
	Text,
	UserGroupSolid,
} from '@bonusly/design-system';
import { useBuildRecognizeURL } from '@bonusly/url';

import { Bonus } from '../../../../types/bonus';
import { Route } from '../../config/router';
import User from '../../types/user';
import { pathWithId } from '../../utils/api';
import { UserAvatar } from './UserAvatar';

export interface UserPopoverProps {
	user: Pick<User, 'display_name' | 'username' | 'profile_pic_url' | 'id'> & {
		custom_properties?: {
			department?: string;
			location?: string;
		};
		last_recognition?: Bonus;
	};
}

export const UserPopover = ({ user }: UserPopoverProps) => {
	const { t } = useTranslation('navigation');
	const recognizeURL = useBuildRecognizeURL();

	return (
		<Portal>
			<PopoverContent borderColor="gray.50">
				<PopoverBody padding={5}>
					<Stack spacing={3}>
						<Flex gap={3} alignItems="center">
							<UserAvatar border="2px solid white" size="xl" user={user} />
							<Box>
								<Text fontSize="md" fontWeight="medium">
									{user.display_name}
								</Text>
								<Text flex={1} fontSize="sm" fontWeight="normal">
									@{user.username}
								</Text>
							</Box>
						</Flex>
						<Divider />
						<Grid gap={3} templateColumns={`${rem(21.6)} 1fr`} alignItems="center">
							{user.custom_properties?.location ? (
								<>
									<LocationMarkerSolid size="lg" />
									<Text fontSize="sm">{user.custom_properties.location}</Text>
								</>
							) : null}
							{user.custom_properties?.department ? (
								<>
									<UserGroupSolid size="lg" />
									<Text fontSize="sm">{user.custom_properties.department}</Text>
								</>
							) : null}
							{user.last_recognition ? (
								<>
									<HeartSolid size="lg" />
									<Text fontSize="sm">
										{t('user_popover.recently_recognized', {
											name: user.last_recognition.receivers[0].display_name,
											count: user.last_recognition.receivers.length,
										})}
									</Text>
								</>
							) : null}
						</Grid>
						<Button as="a" href={recognizeURL(pathWithId(Route.UserProfile, user.id))} textTransform="capitalize">
							{t('user_popover.button_text')}
						</Button>
					</Stack>
				</PopoverBody>
			</PopoverContent>
		</Portal>
	);
};
